import { useTranslation } from 'react-i18next';

import { ISelectOption } from '@private/components';
import { i18n, TFunction } from 'i18next';

import {
  IAnalyticDictionaries,
  IAnalyticDictionariesKeys,
  IAuthDictionaries,
  IAuthDictionariesKeys,
  IBuilderDictionaries,
  IBuilderDictionariesKeys,
  IDictionaries,
  IDictionariesKeys,
  IPaymentMethod,
  IResultCode,
} from 'api/data/types';
import { IFilterData } from 'api/filters/types';
import { useFilterContext } from 'components/Grids/DataGrid/Filters/FilterContext';
import {
  ANALYTICS_DICTIONARY_PREFIX,
  AUTH_DICTIONARY_PREFIX,
  BUILDER_DICTIONARY_PREFIX,
  DICTIONARY_PREFIX,
} from 'components/Grids/DataGrid/Filters/utils/constants';
import { getDictionaryOptionIntlKey } from 'components/Grids/DataGrid/Filters/utils/helpers';
import {
  APPROVAL_BINARY_OPTIONS,
  BINARY_OPTIONS,
  VERIFIED_BINARY_OPTIONS,
} from 'constants/common';
import { useGetFieldNormalizer } from 'hooks/useGetFieldNormalizer';
import {
  useGetAnalyticsDictionaries,
  useGetAuthDictionaries,
  useGetBuilderDictionaries,
  useGetDictionaries,
} from 'queries/data';
import { alphabetSort, getCountryLabelWithCode, getNormalName } from 'utils/common';
import { getRequestFilterOptions } from 'utils/filters';

interface IGetDictionaryOptionsParams
  extends Pick<IUseGetSelectOptionsProps, 'columnKey' | 'type'> {
  dictionary?: IDictionaries;
  i18n: i18n;
  t: TFunction<'translation'>;
}

const getDictionaryOptions = ({
  columnKey,
  dictionary,
  i18n,
  t,
  type,
}: IGetDictionaryOptionsParams) => {
  if (!dictionary) {
    return [];
  }

  const [_, dictionaryName] = type.split('.');

  const optionMapVariants = {
    countryCode: (option: string) => ({
      label: columnKey === 'binCountryCode' ? option : getCountryLabelWithCode(option),
      value: option,
    }),
    currency: (option: string) => ({
      label: option,
      value: option,
    }),
    paymentMethod: ({ description, name }: IPaymentMethod) => ({
      label: description,
      value: name,
    }),
    resultCode: ({ code, description, message }: IResultCode) => ({
      label: `${code} - ${message}${description ? ` (${description})` : ''}`,
      value: code,
    }),
  };

  const baseOptionMapping = (option: string) => {
    const intlKey = getDictionaryOptionIntlKey({ type, value: option });

    return {
      label:
        intlKey && i18n.exists(intlKey) ? t(intlKey as never) : getNormalName(option),
      value: option,
    };
  };

  const optionsList = dictionary[dictionaryName as IDictionariesKeys]?.map(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    optionMapVariants[dictionaryName as keyof typeof optionMapVariants] ||
      baseOptionMapping,
  );

  if (dictionaryName === 'countryCode') {
    return optionsList.sort(alphabetSort);
  }

  return optionsList;
};

interface IGetAnalyticsDictionaryOptionsParams
  extends Pick<IUseGetSelectOptionsProps, 'type'> {
  analyticsDictionary?: IAnalyticDictionaries;
  i18n: i18n;
  t: TFunction<'translation'>;
}

const getAnalyticsDictionaryOptions = ({
  analyticsDictionary,
  i18n,
  t,
  type,
}: IGetAnalyticsDictionaryOptionsParams) => {
  if (!analyticsDictionary) {
    return [];
  }

  const [_, dictionaryName] = type.split('.');

  const options = analyticsDictionary[dictionaryName as IAnalyticDictionariesKeys];

  if (Array.isArray(options)) {
    return options.map((option: string) => {
      const intlKey = getDictionaryOptionIntlKey({ type, value: option });

      return {
        label:
          intlKey && i18n.exists(intlKey) ? t(intlKey as never) : getNormalName(option),
        value: option,
      };
    });
  }

  return [];
};

interface IGetAuthDictionaryOptionsParams
  extends Pick<IUseGetSelectOptionsProps, 'type'> {
  authDictionary?: IAuthDictionaries;
  i18n: i18n;
  t: TFunction<'translation'>;
}

const getAuthDictionaryOptions = ({
  authDictionary,
  i18n,
  t,
  type,
}: IGetAuthDictionaryOptionsParams) => {
  if (!authDictionary) {
    return [];
  }

  const [_, dictionaryName] = type.split('.');

  const options = authDictionary[dictionaryName as IAuthDictionariesKeys];

  if (Array.isArray(options)) {
    return options.map((option: string) => {
      const intlKey = getDictionaryOptionIntlKey({ type, value: option });

      return {
        label:
          intlKey && i18n.exists(intlKey) ? t(intlKey as never) : getNormalName(option),
        value: option,
      };
    });
  }

  return [];
};

interface IGetBuilderDictionaryOptionsParams
  extends Pick<IUseGetSelectOptionsProps, 'type'> {
  builderDictionary?: IBuilderDictionaries;
  i18n: i18n;
  t: TFunction<'translation'>;
}

const getBuilderDictionaryOptions = ({
  builderDictionary,
  i18n,
  t,
  type,
}: IGetBuilderDictionaryOptionsParams) => {
  if (!builderDictionary) {
    return [];
  }

  const [_, dictionaryName] = type.split('.');

  const options = builderDictionary[dictionaryName as IBuilderDictionariesKeys];

  if (Array.isArray(options)) {
    return options.map((option: string) => {
      const intlKey = getDictionaryOptionIntlKey({ type, value: option });

      return {
        label:
          intlKey && i18n.exists(intlKey) ? t(intlKey as never) : getNormalName(option),
        value: option,
      };
    });
  }

  return [];
};

interface IUseGetSelectOptionsProps {
  columnKey: string;
  isRequestFilter: boolean;
  requestFilterData?: IFilterData | null;
  type: string;
}

export const useGetSelectOptions = ({
  columnKey,
  isRequestFilter,
  requestFilterData,
  type,
}: IUseGetSelectOptionsProps) => {
  const { i18n, t } = useTranslation();

  const isDictionary = type.startsWith(DICTIONARY_PREFIX);
  const isAnalyticsDictionary = type.startsWith(ANALYTICS_DICTIONARY_PREFIX);
  const isAuthDictionary = type.startsWith(AUTH_DICTIONARY_PREFIX);
  const isBuilderDictionary = type.startsWith(BUILDER_DICTIONARY_PREFIX);

  const { data: dictionary } = useGetDictionaries();
  const { data: analyticsDictionary } =
    useGetAnalyticsDictionaries(isAnalyticsDictionary);
  const { data: authDictionary } = useGetAuthDictionaries(isAuthDictionary);
  const { data: builderDictionary } = useGetBuilderDictionaries(isBuilderDictionary);

  const { filters } = useFilterContext();
  const { normalizer } = useGetFieldNormalizer();

  if (!type) {
    return [];
  }

  if (isRequestFilter) {
    return getRequestFilterOptions({
      columnFilterValues: filters?.[columnKey]?.value as
        | ISelectOption
        | ISelectOption[]
        | string[],
      columnKey,
      normalizer,
      requestFilterData,
    });
  }

  if (isDictionary) {
    return getDictionaryOptions({
      columnKey,
      dictionary,
      i18n,
      t,
      type,
    });
  }

  if (isAnalyticsDictionary) {
    return getAnalyticsDictionaryOptions({
      analyticsDictionary,
      i18n,
      t,
      type,
    });
  }

  if (isAuthDictionary) {
    return getAuthDictionaryOptions({
      authDictionary,
      i18n,
      t,
      type,
    });
  }

  if (isBuilderDictionary) {
    return getBuilderDictionaryOptions({
      builderDictionary,
      i18n,
      t,
      type,
    });
  }

  if (columnKey === 'verified') {
    return VERIFIED_BINARY_OPTIONS;
  }

  if (columnKey === 'cascadeAfterTimeout') {
    return BINARY_OPTIONS;
  }

  return APPROVAL_BINARY_OPTIONS;
};
